import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { useProductLookup } from '~/features/deliveries/hooks/use-product-lookup';
import type { ProductLookupRow } from '~/features/deliveries/types/delivery-runs.types';
import type { ProductDetailAttribute, ProductOptimization } from '~/types/enrichment';

import { applyCurrentValues } from '../mappers/catalog-operations.mapper';
import { getProductDetail, getProductEnrichment } from '../services/catalog-operations.service';
import type { Product, ProductFilterValues } from '../types/catalog-operations.types';
import {
  buildLookupIdPlan,
  indexLookupRowsByCurationId,
  matchLookupRow,
} from '../utils/delivery-compare-ids';
import { parseVersionSnapshotFields } from '../utils/version-snapshot-fields';

import { useCurationProducts } from './use-curation-products';
import { useProductVersion } from './use-product-versions';

/** Default row-set filter for the compare rail — mirrors the "Newly Enriched
 *  Products" tab's default on the products page, since only enriched products
 *  are meaningful to compare here. */
const RAIL_FILTERS: ProductFilterValues = { needs_attention: false };

/**
 * The compare view's product rail: a page of the batch's curated products,
 * carrying `feedProductVersion` (which archived version each run processed —
 * the pre-enrichment source, per the product decision to use the archived
 * version rather than the live row) and the identity fields the delivery join
 * needs. Same data source and cache slot family as the Products page's
 * Newly Enriched tab.
 */
export function useDeliveryCompareRail(
  curationRefId: string | undefined,
  batchId: string,
  page: number,
  size: number,
  search: string,
) {
  return useCurationProducts({
    curationRefId,
    batchId,
    page,
    size,
    filters: RAIL_FILTERS,
    search,
  });
}

/**
 * Lean (no-payload) delivery lookup for a whole rail page, keyed by every id
 * field that might carry our curation id — see `delivery-compare-ids.ts` for
 * why the join is defensive. Used only for the rail's outcome/sync chips;
 * the per-attribute delivered payload is fetched separately, for the selected
 * product only (see `useDeliveryCompareSelectedDelivery`), per the "lean
 * first, expand one" pattern the delivery contract recommends.
 */
export function useDeliveryCompareRailOutcomes(
  connectionId: string | null | undefined,
  deliveryJobId: string | null | undefined,
  products: readonly Product[],
) {
  const { idType, ids } = buildLookupIdPlan(products);
  const lookup = useProductLookup(
    connectionId ?? '',
    { ids, idType, deliveryJobId: deliveryJobId ?? null },
    !!connectionId && ids.length > 0,
  );
  const rowsByCurationId = lookup.data
    ? indexLookupRowsByCurationId(lookup.data.products)
    : new Map<string, ProductLookupRow>();
  return { ...lookup, rowsByCurationId };
}

/** What the pre-enrichment pane reads, whichever source it came from. */
export interface PreEnrichmentSnapshot {
  source: 'archived' | 'live';
  /** Archived version number, or `null` for the live fallback. */
  version: number | null;
  isLoading: boolean;
  isError: boolean;
  rawFields: Record<string, string>;
  productDetails?: ProductDetailAttribute[];
}

/**
 * Archived version of a feed product matching what the enrichment run
 * actually saw (`product.feedProductVersion`) — the pre-enrichment source per
 * the product decision. Falls back to the LIVE curation detail when no
 * version is recorded (rollout-era runs, or the curation service not yet
 * versioning this subsidiary) so the pane degrades to "current values" rather
 * than staying empty; `source` tells the caller which one it got so the pane
 * can label it honestly ("Archived vN" vs "Live — no archived version on
 * record for this run").
 */
export function usePreEnrichmentSnapshot(
  product: Product | undefined,
  batchId: string,
): PreEnrichmentSnapshot {
  const hasVersion = typeof product?.feedProductVersion === 'number';
  // `hasVersion` narrows `product` itself within each branch below (TS's
  // aliased-condition analysis on the `product?.` check), but not the
  // `feedProductVersion` field's own optionality — it stays `?? null`.
  const versionQuery = useProductVersion(
    hasVersion ? product.id : null,
    hasVersion ? (product.feedProductVersion ?? null) : null,
  );
  const liveQuery = useQuery({
    queryKey: queryKeys.curation.productDetail(product?.id ?? ''),
    queryFn: () => getProductDetail(batchId, product?.id ?? ''),
    enabled: !hasVersion && !!product?.id,
  });

  if (hasVersion) {
    const parsed = versionQuery.data
      ? parseVersionSnapshotFields(versionQuery.data.fields)
      : { rawFields: {} };
    return {
      source: 'archived',
      version: product.feedProductVersion ?? null,
      isLoading: versionQuery.isLoading,
      isError: versionQuery.isError,
      ...parsed,
    };
  }
  return {
    source: 'live',
    version: null,
    isLoading: liveQuery.isLoading,
    isError: liveQuery.isError,
    rawFields: liveQuery.data?.rawFields ?? {},
    // Spread conditionally — `exactOptionalPropertyTypes` forbids assigning an
    // explicit `undefined` to an optional property.
    ...(liveQuery.data?.productDetailsStructured
      ? { productDetails: liveQuery.data.productDetailsStructured }
      : {}),
  };
}

/** Enriched values for the selected product — the same read the product
 *  detail page uses. */
export function useSelectedEnrichment(
  product: Product | undefined,
  connectorCode: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.productEnrichment(
      product?.id ?? '',
      false,
      connectorCode,
    ),
    queryFn: () => getProductEnrichment(product?.id ?? '', false, connectorCode),
    enabled: !!product?.id,
  });
}

/**
 * Merges the selected product's enrichment fields with its pre-enrichment
 * snapshot, producing the `ProductOptimization[]` the attribute table renders
 * two of its three panes from (`currentValue`/`currentAttributes` =
 * pre-enrichment, `value` = enriched). Pure — no fetching.
 */
export function buildSelectedOptimizations(
  optimizations: ProductOptimization[] | undefined,
  preEnrichment: PreEnrichmentSnapshot,
): ProductOptimization[] {
  const withCurrent = applyCurrentValues(optimizations ?? [], preEnrichment.rawFields);
  return withCurrent.map((opt) =>
    opt.contentType === 'product-details'
      ? { ...opt, currentAttributes: preEnrichment.productDetails ?? [] }
      : opt,
  );
}

/**
 * Single-product delivery lookup WITH payload, for the selected product only
 * (per the product decision to fetch delivered values for the selected
 * product, not the whole page). `idType: 'source'` + the product's own
 * curation id — same assumption as the rail's lean lookup, see
 * `delivery-compare-ids.ts`.
 */
export function useDeliveryCompareSelectedDelivery(
  connectionId: string | null | undefined,
  deliveryJobId: string | null | undefined,
  product: Product | undefined,
) {
  const enabled = !!connectionId && !!product?.id;
  const lookup = useProductLookup(
    connectionId ?? '',
    {
      ids: product ? [product.id] : [],
      idType: 'source',
      deliveryJobId: deliveryJobId ?? null,
      includePayload: true,
    },
    enabled,
  );
  const row = product
    ? matchLookupRow(product, indexLookupRowsByCurationId(lookup.data?.products ?? []))
    : undefined;
  return { ...lookup, row };
}
